import type { SessionSummary } from '@perplexicon/api-client';

import { Card, EmptyState, ErrorState, LoadingState } from '../../components/index.js';
import { useCurrentUser } from '../../lib/auth.js';
import { getErrorMessage } from '../../lib/errors.js';
import { formatDateTime } from '../../lib/format-date.js';
import { useSessionHistory } from '../../lib/games.js';
import { opponentNames } from '../../lib/session-display.js';
import { describeSessionOutcome } from '../../lib/session-outcome.js';

/** The viewer's own recorded score for this session, if the game reported one (APP_SPEC §44's "score"). */
function ownScore(entry: SessionSummary, selfId: string): number | null {
  return entry.players.find((p) => p.userId === selfId)?.score ?? null;
}

/**
 * The completed-game history list (APP_SPEC §44, §65 "Platform"). Lives
 * under Settings ("Game History" tab) rather than primary navigation — it
 * is an account/records screen, not a top-level destination. Every field
 * rendered here — game name, opponents, result, completion date, score,
 * and the game's own summary text — comes from the platform's session-list
 * endpoint (`GET /api/game-sessions?status=completed`), which already
 * restricts results to sessions the viewer participated in (privacy and
 * blocking rules are enforced server-side, not by anything rendered here —
 * CLAUDE.md §11). The result label distinguishes a forfeit loss from an
 * ordinary loss and a solo abandonment from either, because
 * `describeSessionOutcome` renders exactly the server's own `result` value
 * rather than re-deriving anything (docs/APP_PLAN.md §12 M6).
 */
export function HistoryPanel() {
  const { data: user } = useCurrentUser();
  const history = useSessionHistory();

  if (!user) {
    return <ErrorState message="Sign in to view your game history." />;
  }

  if (history.isPending) {
    return <LoadingState label="Loading history…" />;
  }

  if (history.isError) {
    return (
      <ErrorState message={getErrorMessage(history.error)} onRetry={() => void history.refetch()} />
    );
  }

  if (history.data.length === 0) {
    return (
      <EmptyState
        illustration="illustrationBooksCandle"
        title="No completed games"
        description="Finished sessions will be recorded here."
      />
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {history.data.map((entry) => {
        const outcome = describeSessionOutcome(entry, user.id);
        const score = ownScore(entry, user.id);
        return (
          <li key={entry.id}>
            <Card className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-textPrimary">{entry.gameName}</p>
                  <p className="text-sm text-textSecondary">vs {opponentNames(entry, user.id)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="font-semibold text-textPrimary">
                    {outcome?.headline ?? entry.summary.headline}
                  </p>
                  <p className="text-xs text-textSecondary">
                    {entry.completedAt ? formatDateTime(entry.completedAt) : ''}
                  </p>
                </div>
              </div>
              {outcome?.detail && <p className="text-sm text-textSecondary">{outcome.detail}</p>}
              {score !== null && <p className="text-sm text-textSecondary">Score: {score}</p>}
            </Card>
          </li>
        );
      })}
    </ul>
  );
}
